/**
 * Schedule Calendar Component
 * Displays scheduled scans in a calendar view with filtering capabilities
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  Target,
  Building2,
  Play,
  Pause,
  Filter,
  Search,
  X,
  RefreshCw,
  AlertCircle,
  Plus,
  Scan,
  FileText,
  Trash2,
  Edit3
} from 'lucide-react';
import { schedules as schedulesApi, workspaces as workspacesApi } from '../api/client';
import { toast } from '../utils/toastUtils';
import { useI18n } from '../i18n';

// Colors for different clients
const CLIENT_COLORS = [
  { bg: 'bg-blue-500/20', border: 'border-blue-500', text: 'text-blue-400' },
  { bg: 'bg-purple-500/20', border: 'border-purple-500', text: 'text-purple-400' },
  { bg: 'bg-cyan-500/20', border: 'border-cyan-500', text: 'text-cyan-400' },
  { bg: 'bg-pink-500/20', border: 'border-pink-500', text: 'text-pink-400' },
  { bg: 'bg-orange-500/20', border: 'border-orange-500', text: 'text-orange-400' },
  { bg: 'bg-teal-500/20', border: 'border-teal-500', text: 'text-teal-400' },
  { bg: 'bg-indigo-500/20', border: 'border-indigo-500', text: 'text-indigo-400' },
  { bg: 'bg-rose-500/20', border: 'border-rose-500', text: 'text-rose-400' },
];

const ScheduleCalendar = ({
  workspaces = [],
  onScheduleClick,
  onClientClick,
  onCreateSchedule,
  onEditSchedule,
  onDeleteSchedule,
  onRunScanNow,
  onGenerateReport,
  showHeader = true,
  initialMonth = new Date().getMonth(),
  initialYear = new Date().getFullYear()
}) => {
  const { t } = useI18n();
  const [currentMonth, setCurrentMonth] = useState(initialMonth);
  const [currentYear, setCurrentYear] = useState(initialYear);
  const [allSchedules, setAllSchedules] = useState([]);
  // Start with loading=false - we only show loading when actively fetching with workspaces
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClients, setSelectedClients] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  // Track current fetch to prevent stale updates from overriding fresh state
  const fetchIdRef = useRef(0);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Day abbreviations array starting with Monday (to match backend convention where 0=Monday)
  const DAYS = [
    t('workspaces.dayMon', 'Lun'),
    t('workspaces.dayTue', 'Mar'),
    t('workspaces.dayWed', 'Mer'),
    t('workspaces.dayThu', 'Jeu'),
    t('workspaces.dayFri', 'Ven'),
    t('workspaces.daySat', 'Sam'),
    t('workspaces.daySun', 'Dim')
  ];

  // Client color mapping
  const clientColors = useMemo(() => {
    const mapping = {};
    workspaces.forEach((ws, idx) => {
      mapping[ws.id] = CLIENT_COLORS[idx % CLIENT_COLORS.length];
    });
    return mapping;
  }, [workspaces]);

  // Fetch all schedules from all workspaces
  const fetchAllSchedules = async (workspacesToFetch, abortSignal, currentFetchId) => {
    // Helper to check if this fetch is still the current one
    const isCurrentFetch = () => fetchIdRef.current === currentFetchId && isMountedRef.current;

    if (!workspacesToFetch || workspacesToFetch.length === 0) {
      if (isCurrentFetch()) {
        setLoading(false);
        setAllSchedules([]);
      }
      return;
    }

    setLoading(true);
    try {
      // Fetch all workspaces in parallel with Promise.allSettled for better performance
      // and to prevent one slow/failing request from blocking others
      const fetchPromises = workspacesToFetch.map(async (workspace) => {
        // Check if aborted before making request
        if (abortSignal?.aborted) {
          return [];
        }
        try {
          const schedules = await schedulesApi.list(workspace.id);
          // Check if aborted after request completes
          if (abortSignal?.aborted) {
            return [];
          }
          if (Array.isArray(schedules)) {
            return schedules.map(schedule => ({
              ...schedule,
              workspace_id: workspace.id,
              workspace_name: workspace.name,
              client_name: workspace.company_name || workspace.name
            }));
          }
          return [];
        } catch (error) {
          // Don't log warnings if aborted
          if (!abortSignal?.aborted) {
            console.warn(`Could not fetch schedules for workspace ${workspace.id}`);
          }
          return [];
        }
      });

      // Add a 30-second timeout to prevent infinite loading
      let timeoutId;
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error('Timeout')), 30000);
      });

      try {
        const results = await Promise.race([
          Promise.allSettled(fetchPromises),
          timeoutPromise
        ]);

        // Clear timeout if Promise.allSettled won the race
        clearTimeout(timeoutId);

        // Check if this fetch is still current before updating state
        if (!isCurrentFetch()) {
          return;
        }

        // Combine all successful results
        const allData = results
          .filter(result => result.status === 'fulfilled')
          .flatMap(result => result.value || []);

        setAllSchedules(allData);
      } catch (error) {
        // Clear timeout on error as well
        clearTimeout(timeoutId);
        throw error;
      }
    } catch (error) {
      // Don't update state if this fetch is no longer current
      if (!isCurrentFetch()) {
        return;
      }
      // Timeout or other error - show empty state instead of infinite loading
      console.error('Error fetching schedules:', error);
      if (error.message !== 'Timeout') {
        toast.error('Erreur lors du chargement des planifications');
      }
      setAllSchedules([]);
    } finally {
      // Only update loading state if this fetch is still current
      // This prevents a stale fetch from setting loading=false while a new fetch is in progress
      if (isCurrentFetch()) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // Increment fetch ID to invalidate any previous fetch
    fetchIdRef.current += 1;
    const currentFetchId = fetchIdRef.current;

    // Create an AbortController to cancel fetch if component unmounts or dependencies change
    const abortController = new AbortController();

    fetchAllSchedules(workspaces, abortController.signal, currentFetchId);

    // Failsafe: ensure loading state is cleared after 10 seconds max
    // This prevents loading from being stuck indefinitely due to race conditions or network issues
    const failsafeTimer = setTimeout(() => {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }, 10000);

    // Cleanup function: abort any in-flight requests when dependencies change or unmount
    return () => {
      abortController.abort();
      clearTimeout(failsafeTimer);
    };
  }, [workspaces]);

  // Calculate days in month
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get first day of month (0 = Monday, 6 = Sunday - matching DAYS array)
  const getFirstDayOfMonth = (month, year) => {
    const jsDay = new Date(year, month, 1).getDay(); // JS: 0=Sunday, 1=Monday, ..., 6=Saturday
    // Convert to Monday-first: 0=Monday, 1=Tuesday, ..., 6=Sunday
    return jsDay === 0 ? 6 : jsDay - 1;
  };

  // Navigate months
  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
  };

  // Parse schedule recurrence to get dates
  const getScheduleDates = (schedule, month, year) => {
    const dates = [];
    const daysInMonth = getDaysInMonth(month, year);

    // Parse next_run or use frequency
    if (schedule.next_run) {
      const nextRun = new Date(schedule.next_run);
      if (nextRun.getMonth() === month && nextRun.getFullYear() === year) {
        dates.push(nextRun.getDate());
      }
    }

    // Parse frequency for recurring schedules
    if (schedule.frequency) {
      const freq = schedule.frequency.toLowerCase();
      const startDate = new Date(schedule.created_at || Date.now());

      if (freq === 'daily') {
        // Add all days of the month
        for (let d = 1; d <= daysInMonth; d++) {
          if (!dates.includes(d)) dates.push(d);
        }
      } else if (freq === 'weekly') {
        // Add same day each week
        const dayOfWeek = schedule.day_of_week || startDate.getDay();
        for (let d = 1; d <= daysInMonth; d++) {
          const date = new Date(year, month, d);
          if (date.getDay() === dayOfWeek && !dates.includes(d)) {
            dates.push(d);
          }
        }
      } else if (freq === 'monthly') {
        // Add same day each month
        const dayOfMonth = schedule.day_of_month || startDate.getDate();
        if (dayOfMonth <= daysInMonth && !dates.includes(dayOfMonth)) {
          dates.push(dayOfMonth);
        }
      }
    }

    return dates;
  };

  // Get schedules for a specific day
  const getSchedulesForDay = (day) => {
    const filteredSchedules = allSchedules.filter(schedule => {
      // Filter by selected clients
      if (selectedClients.length > 0 && !selectedClients.includes(schedule.workspace_id)) {
        return false;
      }

      // Filter by search term
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesName = schedule.name?.toLowerCase().includes(search);
        const matchesClient = schedule.client_name?.toLowerCase().includes(search);
        const matchesTarget = schedule.target?.toLowerCase().includes(search);
        if (!matchesName && !matchesClient && !matchesTarget) {
          return false;
        }
      }

      // Check if schedule falls on this day
      const dates = getScheduleDates(schedule, currentMonth, currentYear);
      return dates.includes(day);
    });

    return filteredSchedules;
  };

  // Toggle client filter
  const toggleClientFilter = (workspaceId) => {
    setSelectedClients(prev => {
      if (prev.includes(workspaceId)) {
        return prev.filter(id => id !== workspaceId);
      } else {
        return [...prev, workspaceId];
      }
    });
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedClients([]);
    setSearchTerm('');
  };

  // Build calendar grid
  const buildCalendarGrid = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const grid = [];
    const today = new Date();
    const isCurrentMonth = today.getMonth() === currentMonth && today.getFullYear() === currentYear;

    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      grid.push(null);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const schedules = getSchedulesForDay(day);
      const isToday = isCurrentMonth && today.getDate() === day;

      grid.push({
        day,
        schedules,
        isToday
      });
    }

    return grid;
  };

  const calendarGrid = buildCalendarGrid();

  // Render day cell
  const renderDayCell = (cell, idx) => {
    if (!cell) {
      return <div key={`empty-${idx}`} className="min-h-[100px] bg-brand-black/30" />;
    }

    const { day, schedules, isToday } = cell;
    const hasSchedules = schedules.length > 0;

    return (
      <div
        key={day}
        onClick={() => setSelectedDay({ day, schedules, date: new Date(currentYear, currentMonth, day) })}
        className={`min-h-[100px] border border-gray-800 p-2 transition-colors cursor-pointer hover:border-brand-green/30 ${
          isToday ? 'bg-brand-green/10 border-brand-green/50' : 'bg-brand-black-light'
        }`}
      >
        {/* Day number */}
        <div className={`text-sm font-medium mb-1 text-center ${isToday ? 'text-brand-green' : 'text-gray-400'}`}>
          {day}
        </div>

        {/* Schedules preview */}
        <div className="space-y-1">
          {schedules.slice(0, 3).map((schedule, sIdx) => {
            const colors = clientColors[schedule.workspace_id] || CLIENT_COLORS[0];
            return (
              <div
                key={`${schedule.id}-${sIdx}`}
                className={`text-xs px-1 py-0.5 rounded truncate ${colors.bg} ${colors.text} border-l-2 ${colors.border}`}
                title={`${schedule.name} - ${schedule.client_name}`}
              >
                {schedule.name || schedule.target}
              </div>
            );
          })}
          {schedules.length > 3 && (
            <div className="text-xs text-gray-500">
              +{schedules.length - 3} autres
            </div>
          )}
        </div>
      </div>
    );
  };

  // Day detail modal with actions
  const DayDetailModal = () => {
    if (!selectedDay) return null;

    const hasSchedules = selectedDay.schedules.length > 0;
    const formattedDate = selectedDay.date
      ? selectedDay.date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
      : new Date(currentYear, currentMonth, selectedDay.day).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setSelectedDay(null)}>
        <div
          className="bg-brand-black-light border border-gray-700 rounded-xl max-w-lg w-full max-h-[80vh] overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-4 border-b border-gray-700 flex items-center justify-between bg-brand-black">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-brand-green/20 rounded-lg">
                <Calendar className="w-5 h-5 text-brand-green" />
              </div>
              <div>
                <span className="text-white font-medium block capitalize">
                  {formattedDate}
                </span>
                <span className="text-gray-400 text-sm">
                  {hasSchedules ? `${selectedDay.schedules.length} planification${selectedDay.schedules.length > 1 ? 's' : ''}` : 'Aucune planification'}
                </span>
              </div>
            </div>
            <button
              onClick={() => setSelectedDay(null)}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Quick Actions */}
          <div className="p-4 border-b border-gray-700 bg-brand-black/50">
            <h4 className="text-sm font-medium text-gray-400 mb-3">Actions rapides</h4>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  onCreateSchedule && onCreateSchedule(selectedDay.date);
                  setSelectedDay(null);
                }}
                className="flex items-center gap-2 px-3 py-2 bg-brand-green/20 text-brand-green border border-brand-green/50 rounded-lg hover:bg-brand-green/30 transition-all text-sm"
              >
                <Plus className="w-4 h-4" />
                Planifier un scan
              </button>
              <button
                onClick={() => {
                  onRunScanNow && onRunScanNow(selectedDay.date);
                  setSelectedDay(null);
                }}
                className="flex items-center gap-2 px-3 py-2 bg-blue-500/20 text-blue-400 border border-blue-500/50 rounded-lg hover:bg-blue-500/30 transition-all text-sm"
              >
                <Scan className="w-4 h-4" />
                Lancer un scan
              </button>
              <button
                onClick={() => {
                  onGenerateReport && onGenerateReport(selectedDay.date);
                  setSelectedDay(null);
                }}
                className="flex items-center gap-2 px-3 py-2 bg-purple-500/20 text-purple-400 border border-purple-500/50 rounded-lg hover:bg-purple-500/30 transition-all text-sm"
              >
                <FileText className="w-4 h-4" />
                Générer rapport
              </button>
              {hasSchedules && (
                <button
                  onClick={() => {
                    // Toggle all schedules for this day
                    selectedDay.schedules.forEach(s => onEditSchedule && onEditSchedule(s));
                    setSelectedDay(null);
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-orange-500/20 text-orange-400 border border-orange-500/50 rounded-lg hover:bg-orange-500/30 transition-all text-sm"
                >
                  <Edit3 className="w-4 h-4" />
                  Modifier
                </button>
              )}
            </div>
          </div>

          {/* Schedules list */}
          {hasSchedules ? (
            <div className="p-4 space-y-3 max-h-[40vh] overflow-y-auto">
              <h4 className="text-sm font-medium text-gray-400 mb-2">Planifications</h4>
              {selectedDay.schedules.map((schedule, idx) => {
                const colors = clientColors[schedule.workspace_id] || CLIENT_COLORS[0];
                return (
                  <div
                    key={`${schedule.id}-${idx}`}
                    className={`p-3 rounded-lg border ${colors.border} ${colors.bg}`}
                  >
                    <div className="flex items-start justify-between">
                      <div
                        className="flex-1 cursor-pointer"
                        onClick={() => {
                          onScheduleClick && onScheduleClick(schedule);
                          setSelectedDay(null);
                        }}
                      >
                        <div className={`font-medium ${colors.text}`}>
                          {schedule.name || 'Planification'}
                        </div>
                        <div className="text-sm text-gray-400 mt-1">
                          <Building2 className="w-3 h-3 inline mr-1" />
                          {schedule.client_name}
                        </div>
                        {schedule.target && (
                          <div className="text-sm text-gray-500 mt-1">
                            <Target className="w-3 h-3 inline mr-1" />
                            {schedule.target}
                          </div>
                        )}
                        {schedule.scheduled_time && (
                          <div className="text-sm text-gray-500 mt-1">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {schedule.scheduled_time}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {schedule.is_paused ? (
                          <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded">
                            <Pause className="w-3 h-3 inline mr-1" />
                            Pause
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded">
                            <Play className="w-3 h-3 inline mr-1" />
                            Actif
                          </span>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditSchedule && onEditSchedule(schedule);
                          }}
                          className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded"
                          title="Modifier"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Supprimer cette planification ?')) {
                              onDeleteSchedule && onDeleteSchedule(schedule);
                            }
                          }}
                          className="p-1 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-800 rounded-full flex items-center justify-center">
                <Calendar className="w-8 h-8 text-gray-600" />
              </div>
              <p className="text-gray-400 mb-2">Aucune planification pour cette date</p>
              <p className="text-gray-500 text-sm">Utilisez les actions rapides ci-dessus pour planifier un scan</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-brand-black-light rounded-xl border border-gray-700 overflow-hidden">
      {/* Header */}
      {showHeader && (
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="w-6 h-6 text-brand-green" />
              <h2 className="text-xl font-bold text-white">Calendrier des Scans</h2>
            </div>
            <button
              onClick={() => {
                fetchIdRef.current += 1;
                fetchAllSchedules(workspaces, null, fetchIdRef.current);
              }}
              disabled={loading}
              className="p-2 text-gray-400 hover:text-brand-green rounded-lg hover:bg-brand-black"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="p-4 border-b border-gray-700 space-y-3">
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Rechercher un scan, client, cible…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-brand-green focus:outline-none text-sm"
            />
          </div>

          {/* Filter toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg border ${
              showFilters || selectedClients.length > 0
                ? 'border-brand-green bg-brand-green/10 text-brand-green'
                : 'border-gray-700 text-gray-400 hover:text-white'
            }`}
          >
            <Filter className="w-5 h-5" />
          </button>

          {/* Clear filters */}
          {(selectedClients.length > 0 || searchTerm) && (
            <button
              onClick={clearFilters}
              className="px-3 py-2 text-sm text-gray-400 hover:text-white"
            >
              Effacer
            </button>
          )}
        </div>

        {/* Client filters */}
        {showFilters && (
          <div className="flex flex-wrap gap-2">
            {workspaces.map((workspace) => {
              const colors = clientColors[workspace.id] || CLIENT_COLORS[0];
              const isSelected = selectedClients.includes(workspace.id);
              return (
                <button
                  key={workspace.id}
                  onClick={() => toggleClientFilter(workspace.id)}
                  className={`px-3 py-1 text-sm rounded-full transition-all ${
                    isSelected
                      ? `${colors.bg} ${colors.text} border ${colors.border}`
                      : 'bg-brand-black text-gray-400 border border-gray-700 hover:border-gray-600'
                  }`}
                >
                  {workspace.company_name || workspace.name}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Calendar Navigation */}
      <div className="p-4 flex items-center justify-between border-b border-gray-700">
        <button
          onClick={prevMonth}
          className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-brand-black"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-4">
          <span className="text-lg font-medium text-white">
            {new Date(currentYear, currentMonth, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
          </span>
          <button
            onClick={goToToday}
            className="px-3 py-1 text-sm text-brand-green border border-brand-green/50 rounded-lg hover:bg-brand-green/10 transition-colors"
          >
            Aujourd'hui
          </button>
        </div>

        <button
          onClick={nextMonth}
          className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-brand-black"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Calendar Grid */}
      {loading ? (
        <div className="p-12 text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-brand-green mb-3" />
          <p className="text-gray-400">Chargement du calendrier...</p>
        </div>
      ) : (
        <>
          {/* Day headers */}
          <div className="grid grid-cols-7">
            {DAYS.map(day => (
              <div key={day} className="p-2 text-center text-sm font-medium text-gray-500 bg-brand-black border-b border-gray-800">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar cells */}
          <div className="grid grid-cols-7">
            {calendarGrid.map((cell, idx) => renderDayCell(cell, idx))}
          </div>
        </>
      )}

      {/* Legend */}
      <div className="p-4 border-t border-gray-700">
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-xs text-gray-500">Clients :</span>
          {workspaces.slice(0, 8).map((workspace) => {
            const colors = clientColors[workspace.id] || CLIENT_COLORS[0];
            return (
              <div
                key={workspace.id}
                onClick={() => onClientClick && onClientClick(workspace)}
                className={`flex items-center gap-1 text-xs cursor-pointer hover:opacity-80 ${colors.text}`}
              >
                <div className={`w-2 h-2 rounded-full ${colors.bg} border ${colors.border}`} />
                <span>{workspace.company_name || workspace.name}</span>
              </div>
            );
          })}
          {workspaces.length > 8 && (
            <span className="text-xs text-gray-500">+{workspaces.length - 8} autres</span>
          )}
        </div>
      </div>

      {/* No schedules message */}
      {!loading && allSchedules.length === 0 && (
        <div className="p-8 text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-gray-600 mb-3" />
          <p className="text-gray-400">Aucune planification</p>
          <p className="text-sm text-gray-500 mt-1">
            Créer des planifications pour voir les scans dans le calendrier
          </p>
        </div>
      )}

      {/* Day detail modal */}
      <DayDetailModal />
    </div>
  );
};

export default ScheduleCalendar;
